/**
 * Iter-114 — Generic "Flag this item" dialog.
 *
 * Opens from any flag-eligible surface (image detail, scene detail,
 * character detail, supervisor notes, change docs, schedule).
 * Caller passes the entity_type + entity_id; the dialog handles
 * recipient picking + optional message + submission.
 *
 * Recipient picking models the spec exactly:
 *   - "Whole team" toggle → broadcasts to every member except the
 *     sender (the backend resolves recipient_team=true that way).
 *   - Otherwise: multi-select chip list of project members.
 *
 * No threaded replies, no chat — once submitted the dialog closes.
 * The flag itself is the only attention surface.
 */

import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, Check, Loader2, Users } from "lucide-react";
import { toast } from "sonner";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useApiBase } from "../context/ApiBaseContext";


export default function FlagDialog({
    projectId,
    entityType,
    entityId,
    entityLabel,
    open,
    onOpenChange,
    onCreated,
}) {
    const { user: me } = useAuth();
    const apiBase = useApiBase();
    const [members, setMembers] = useState([]);
    const [recipientIds, setRecipientIds] = useState([]);
    const [team, setTeam] = useState(false);
    const [note, setNote] = useState("");
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!open || !projectId) return;
        let alive = true;
        (async () => {
            try {
                const { data } = await api.get(`${apiBase}/${projectId}/members`);
                if (!alive) return;
                setMembers(data || []);
            } catch (_) {
                /* noop */
            }
        })();
        return () => { alive = false; };
    }, [open, projectId, apiBase]);

    // Reset on each open.
    useEffect(() => {
        if (open) {
            setRecipientIds([]);
            setTeam(false);
            setNote("");
        }
    }, [open]);

    const eligible = useMemo(
        () => members.filter((m) => m.user_id !== me?.id),
        [members, me?.id]
    );

    const submit = async () => {
        if (submitting) return;
        if (!team && recipientIds.length === 0) {
            toast.error("Pick at least one recipient or send to the team.");
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await api.post(`${apiBase}/${projectId}/flags`, {
                entity_type: entityType,
                entity_id: entityId,
                note: note.trim() || null,
                recipient_user_ids: team ? [] : recipientIds,
                recipient_team: team,
            });
            toast.success("Flag sent");
            if (onCreated) onCreated(data);
            onOpenChange(false);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSubmitting(false);
        }
    };

    const toggleRecipient = (uid) => {
        setRecipientIds((cur) =>
            cur.includes(uid) ? cur.filter((x) => x !== uid) : [...cur, uid]
        );
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                data-testid="flag-dialog"
                className="max-w-md p-0 overflow-hidden"
            >
                <DialogHeader className="px-5 pt-5">
                    <DialogTitle className="flex items-center gap-2 text-base">
                        <AlertTriangle
                            size={16}
                            className="text-amber-400"
                        />
                        Flag {entityLabel || "item"}
                    </DialogTitle>
                </DialogHeader>
                <div className="px-5 py-4 space-y-4">
                    <div>
                        <span className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                            Send to
                        </span>
                        <button
                            type="button"
                            data-testid="flag-team-toggle"
                            data-active={team}
                            onClick={() => setTeam((t) => !t)}
                            className="mt-2 w-full instant-tap segmented-button rounded-lg border border-white/10 px-3 py-2 flex items-center justify-between hover:bg-white/5"
                        >
                            <span className="flex items-center gap-2 text-sm">
                                <Users size={14} />
                                Whole team
                                <span className="text-[10px] text-neutral-500">
                                    ({eligible.length})
                                </span>
                            </span>
                            <span
                                className={`h-4 w-4 rounded border ${
                                    team
                                        ? "bg-amber-400 border-amber-400 flex items-center justify-center text-neutral-900"
                                        : "border-white/20"
                                }`}
                            >
                                {team ? <Check size={11} /> : null}
                            </span>
                        </button>
                        {!team ? (
                            <div
                                data-testid="flag-recipient-list"
                                className="mt-2 max-h-44 overflow-y-auto space-y-1"
                            >
                                {eligible.length === 0 ? (
                                    <p className="text-xs text-neutral-500 px-1 py-3">
                                        No other members on this project yet —
                                        invite collaborators to flag them.
                                    </p>
                                ) : (
                                    eligible.map((m) => {
                                        const checked = recipientIds.includes(
                                            m.user_id
                                        );
                                        return (
                                            <button
                                                key={m.user_id}
                                                type="button"
                                                data-testid={`flag-recipient-${m.user_id}`}
                                                data-active={checked}
                                                onClick={() =>
                                                    toggleRecipient(m.user_id)
                                                }
                                                className="instant-tap w-full flex items-center justify-between rounded-md px-2.5 py-2 text-sm hover:bg-white/5 border border-transparent data-[active=true]:border-amber-500/40 data-[active=true]:bg-amber-500/8"
                                            >
                                                <span className="flex flex-col items-start text-left">
                                                    <span className="text-white text-sm">
                                                        {m.name || m.email}
                                                    </span>
                                                    <span className="text-[10px] text-neutral-500">
                                                        {m.role || "member"}
                                                    </span>
                                                </span>
                                                <span
                                                    className={`h-4 w-4 rounded border ${
                                                        checked
                                                            ? "bg-amber-400 border-amber-400 flex items-center justify-center text-neutral-900"
                                                            : "border-white/20"
                                                    }`}
                                                >
                                                    {checked ? (
                                                        <Check size={11} />
                                                    ) : null}
                                                </span>
                                            </button>
                                        );
                                    })
                                )}
                            </div>
                        ) : null}
                    </div>
                    <div>
                        <label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                            Message (optional)
                        </label>
                        <Textarea
                            data-testid="flag-note-input"
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            placeholder="What needs attention? Keep it short."
                            rows={3}
                            className="mt-2 bg-secondary border-white/10 text-sm"
                        />
                    </div>
                </div>
                <DialogFooter className="px-5 py-4 bg-card/50 border-t border-white/10 gap-2">
                    <Button
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={submitting}
                        data-testid="flag-cancel"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={submit}
                        disabled={
                            submitting ||
                            (!team && recipientIds.length === 0)
                        }
                        data-testid="flag-submit"
                    >
                        {submitting ? (
                            <Loader2 size={14} className="mr-2 animate-spin" />
                        ) : (
                            <AlertTriangle size={14} className="mr-2" />
                        )}
                        Send flag
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
